import { FC, useEffect, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  FramerTooltip,
  GroupTitleTemplate,
  SwitchTemplate,
  TemplateWrapper,
  TextareaTemplate,
  TextboxTemplate,
} from "@ryde/controls";
import {
  IBulkPassengersMessageProps,
  IInjectedDrawerProps,
  onActivateKeyDown,
  SHOWN_PASSENGER_NAMES_LENGTH,
  useHasPermissions,
} from "@ryde/shared";

import { LayoutRightLoader } from "../../../right";
import { CommonDefaultDrawer } from "../common-default-drawer";
import { BaseDrawer, DrawerFormWrapperStyled, FormFooter } from "../drawer";
import {
  BulkPassengersMessageCountStyled,
  BulkPassengersMessageFormFieldsEnum,
  BulkPassengersMessagePassengersListStyled,
  getPassengersNames,
  getPreviewMessage,
  PassengersMessageAvailableTemplateStyled,
  passengersMessageKeysMappedTemplates,
  RidesParticipationFilter,
  useBulkPassengersMessageDrawer,
} from "./parts";

export const BulkPassengersMessageDrawer: FC<
  IBulkPassengersMessageProps & IInjectedDrawerProps
> = ({
  onCloseDrawer,
  drawerNumber,
  selectedPassengersIds,
  hasRecipientsDerivedFromRides,
}) => {
  const { t } = useTranslation();
  const { hasPermissions: hasPassengerSendMessage } = useHasPermissions({
    entity: "passenger",
    action: "send_message",
  });
  const {
    methods,
    isSaveDisabled,
    recipients,
    isPreviewingRecipients,
    isLoading,
    onSaveClick,
  } = useBulkPassengersMessageDrawer({
    selectedPassengersIds,
    hasRecipientsDerivedFromRides,
  });

  const { watch, setValue } = methods;

  const isSendViaEmailValueWatch = watch(
    BulkPassengersMessageFormFieldsEnum.isEmail,
  );
  const messageValueWatch = watch(BulkPassengersMessageFormFieldsEnum.message);

  const passengerNames = useMemo(
    () => getPassengersNames(recipients),
    [recipients],
  );

  // Short-codes preview renders against the first actual recipient, which the filter can change.
  const previewMessageComputed = useMemo(() => {
    return getPreviewMessage(messageValueWatch, recipients);
  }, [messageValueWatch, recipients]);

  const handleSaveClick = useEvent(() => {
    onSaveClick();
  });

  useEffect(() => {
    setValue(
      BulkPassengersMessageFormFieldsEnum.preview,
      previewMessageComputed,
    );
  }, [previewMessageComputed, setValue]);

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        title={t(`passenger.SEND_MESSAGES_TO_PASSENGERS`)}
        onCloseDrawer={onCloseDrawer}
        Footer={
          <FormFooter
            cancelLabel={t("general.CLOSE")}
            onCancelClick={onCloseDrawer}
            onSaveClick={hasPassengerSendMessage ? handleSaveClick : undefined}
            isSaveDisabled={isSaveDisabled || !hasPassengerSendMessage}
          />
        }
      >
        <FormProvider {...methods}>
          {!isLoading && hasPassengerSendMessage && (
            <DrawerFormWrapperStyled>
              {!hasRecipientsDerivedFromRides && <RidesParticipationFilter />}

              <BulkPassengersMessageCountStyled
                aria-live="polite"
                aria-busy={isPreviewingRecipients}
              >
                <span>
                  {t(
                    "passenger.sendPassengerMessageForm.passengersReceivingMessage",
                  )}
                </span>
                <span>{recipients.length}</span>
              </BulkPassengersMessageCountStyled>
              <BulkPassengersMessagePassengersListStyled
                aria-live="polite"
                aria-busy={isPreviewingRecipients}
              >
                <span>{passengerNames}</span>
                <span>
                  {recipients.length > SHOWN_PASSENGER_NAMES_LENGTH
                    ? `+${recipients.length - SHOWN_PASSENGER_NAMES_LENGTH}`
                    : ""}
                </span>
              </BulkPassengersMessagePassengersListStyled>

              <TemplateWrapper>
                <GroupTitleTemplate
                  name={"details_title"}
                  labelTranslated={t(
                    "passenger.sendPassengerMessageForm.details",
                  )}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <SwitchTemplate
                  name={BulkPassengersMessageFormFieldsEnum.isPush}
                  labelTranslated={t(
                    "passenger.sendPassengerMessageForm.sendViaPush",
                  )}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <SwitchTemplate
                  name={BulkPassengersMessageFormFieldsEnum.isSms}
                  labelTranslated={t(
                    "passenger.sendPassengerMessageForm.sendViaSms",
                  )}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <SwitchTemplate
                  name={BulkPassengersMessageFormFieldsEnum.isEmail}
                  labelTranslated={t(
                    "passenger.sendPassengerMessageForm.sendViaEmail",
                  )}
                />
              </TemplateWrapper>

              {isSendViaEmailValueWatch && (
                <TemplateWrapper>
                  <TextboxTemplate
                    name={BulkPassengersMessageFormFieldsEnum.subject}
                    labelTranslated={t(
                      "passenger.sendPassengerMessageForm.subject",
                    )}
                  />
                </TemplateWrapper>
              )}

              <TemplateWrapper>
                <TextareaTemplate
                  name={BulkPassengersMessageFormFieldsEnum.preview}
                  labelTranslated={t(
                    "passenger.sendPassengerMessageForm.preview",
                  )}
                  formAttributes={{
                    disabled: true,
                  }}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <TextareaTemplate
                  name={BulkPassengersMessageFormFieldsEnum.message}
                  labelTranslated={t(
                    "passenger.sendPassengerMessageForm.message",
                  )}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <PassengersMessageAvailableTemplateStyled>
                  {Object.entries(passengersMessageKeysMappedTemplates).map(
                    ([key, translationPath]) => (
                      <FramerTooltip key={key} content={t(translationPath)}>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            setValue(
                              BulkPassengersMessageFormFieldsEnum.message,
                              messageValueWatch + `{{{ ${key} }}} `,
                              { shouldDirty: true, shouldValidate: true },
                            )
                          }
                          onKeyDown={onActivateKeyDown(() =>
                            setValue(
                              BulkPassengersMessageFormFieldsEnum.message,
                              messageValueWatch + `{{{ ${key} }}} `,
                              { shouldDirty: true, shouldValidate: true },
                            ),
                          )}
                          className="item pointer"
                        >{`{{{ ${t(translationPath)} }}}`}</span>
                      </FramerTooltip>
                    ),
                  )}
                </PassengersMessageAvailableTemplateStyled>
              </TemplateWrapper>
            </DrawerFormWrapperStyled>
          )}

          {isLoading && (
            <LayoutRightLoader>{t(`ui.LOADING_DATA`)}...</LayoutRightLoader>
          )}
        </FormProvider>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
